import React, { useState, useEffect, useCallback, useRef } from "react";
import fleetService from "../services/FleetService";
import FleetBookingsOrganized from "../components/FleetBookingsOrganized";
import VanAssignmentForm from "../components/VanAssignmentForm";
import BulkVanBlockModal from "../components/BulkVanBlockModal";
import { toast } from "react-hot-toast";
import "./FleetManagement.css";
import axios from "axios";
import { formatDateShort } from "../utils/format";
import { PRIMARY_API_URL } from "../config/api";
import { getAuthToken, setupAuthHeaders } from '../utils/authUtils';

const FleetManagement = () => {
  // Debug component renders
  console.log('🔄 FleetManagement render:', {
    timestamp: new Date().toISOString(),
    renderCount: ++FleetManagement.renderCount || (FleetManagement.renderCount = 1)
  });

  // --- State ---
  const [bookings, setBookings] = useState([]);
  const [vans, setVans] = useState([]);
  const [vanBlocks, setVanBlocks] = useState([]);

  const [assignments, setAssignments] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingVans, setIsLoadingVans] = useState(false);
  const [isLoadingBookings, setIsLoadingBookings] = useState(false);
  const [error, setError] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [isUnassigning, setIsUnassigning] = useState(false);
  
  // Modal states
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [isAssignModalOpen, setIsAssignModalOpen] = useState(false);
  const [selectedVan, setSelectedVan] = useState(null);
  const [lastRefreshTime, setLastRefreshTime] = useState(null);
  const [expandedVans, setExpandedVans] = useState({});
  const [isBulkBlockModalOpen, setIsBulkBlockModalOpen] = useState(false);
  
  // Add the missing polling interval ref
  const pollingIntervalRef = useRef(null);
  
  const BASE_URL = PRIMARY_API_URL;

  const loadedAssignmentsRef = useRef({ current: {} });

  // Setup authentication headers
  const setupAuth = useCallback(async () => {
    const token = await getAuthToken();
    
    if (!token) {
      console.error("No token found");
      return false;
    }

    // Setup auth headers for axios
    await setupAuthHeaders(axios);
    return true;
  }, []);

  // Fetch vans - more reliable method with retries
  const fetchVans = useCallback(async (retries = 2) => {
    if (!(await setupAuth())) {
      throw new Error("Authentication required");
    }
    
    setIsLoadingVans(true);
    
    try {
      console.log("Fetching vans...");
      const timestamp = Date.now();
      const response = await axios.get(`${BASE_URL}/api/fleet/vans?t=${timestamp}`);
      
      if (response.data && Array.isArray(response.data)) {
        // Ensure all vans have capacity of 7 (for consistency)
        const processedVans = response.data.map(van => ({
          ...van,
          capacity: van.capacity || 7
        }));
        console.log(`Successfully loaded ${processedVans.length} vans`);
        return processedVans;
      } else {
        throw new Error("Invalid van data format");
      }
    } catch (error) {
      console.error("Error fetching vans:", error);
      
      if (retries > 0) {
        console.log(`Retrying van fetch... (${retries} retries left)`);
        await new Promise(resolve => setTimeout(resolve, 1000));
        return fetchVans(retries - 1);
      }
      
      throw new Error(`Failed to fetch vans: ${error.message}`);
    } finally {
      setIsLoadingVans(false);
    }
  }, [setupAuth]);

  // Fetch bookings - more reliable method with retries
  const fetchBookings = useCallback(async (retries = 2) => {
    if (!(await setupAuth())) {
      throw new Error("Authentication required");
    }
    
    setIsLoadingBookings(true);
    
    try {
      console.log("Fetching bookings...");
      const timestamp = Date.now();
      const response = await axios.get(`${BASE_URL}/api/bookings?t=${timestamp}`);
      
      if (response.data && Array.isArray(response.data)) {
        // Show all bookings for Fleet Management (including cancelled/completed)
        const allBookings = response.data;
        console.log(`Successfully loaded ${allBookings.length} bookings`);
        return allBookings;
      } else {
        throw new Error("Invalid booking data format");
      }
    } catch (error) {
      console.error("Error fetching bookings:", error);
      
      if (retries > 0) {
        console.log(`Retrying booking fetch... (${retries} retries left)`);
        await new Promise(resolve => setTimeout(resolve, 1000));
        return fetchBookings(retries - 1);
      }
      
      throw new Error(`Failed to fetch bookings: ${error.message}`);
    } finally {
      setIsLoadingBookings(false);
    }
  }, [setupAuth]);

  // Fetch assignments for a booking
  const fetchAssignmentsForBooking = useCallback(async (bookingId, retries = 2) => {
    if (!(await setupAuth())) {
      throw new Error("Authentication required");
    }
    
    try {
      console.log(`Fetching assignments for booking ${bookingId}...`);
      const timestamp = Date.now();
      const response = await axios.get(`${BASE_URL}/api/bookings/${bookingId}/assignments?t=${timestamp}`);
      
      if (response.data) {
        console.log(`Found ${response.data.length} assignments for booking ${bookingId}`);
        return response.data;
      } else {
        console.log(`No assignments found for booking ${bookingId}`);
        return [];
      }
    } catch (error) {
      console.error(`Error fetching assignments for booking ${bookingId}:`, error);
      
      if (retries > 0) {
        console.log(`Retrying assignment fetch... (${retries} retries left)`);
        await new Promise(resolve => setTimeout(resolve, 1000));
        return fetchAssignmentsForBooking(bookingId, retries - 1);
      }
      
      console.warn(`Failed to fetch assignments for booking ${bookingId} after retries`);
      return [];
    }
  }, [setupAuth]);

  // Fetch van blocks
  const fetchVanBlocks = useCallback(async (retries = 2) => {
    if (!(await setupAuth())) {
      throw new Error("Authentication required");
    }
    
    try {
      console.log("Fetching van blocks...");
      const timestamp = Date.now();
      const response = await axios.get(`${BASE_URL}/api/fleet/vans/blocks?t=${timestamp}`);
      
      if (response.data && response.data.blocks) {
        console.log(`Successfully loaded ${response.data.blocks.length} van blocks`);
        return response.data.blocks;
      } else {
        console.log("No van blocks found");
        return [];
      }
    } catch (error) {
      console.error("Error fetching van blocks:", error);
      
      if (retries > 0) {
        console.log(`Retrying van blocks fetch... (${retries} retries left)`);
        await new Promise(resolve => setTimeout(resolve, 1000));
        return fetchVanBlocks(retries - 1);
      }
      
      console.warn("Failed to fetch van blocks after retries");
      return [];
    }
  }, [setupAuth]);

  // Load all data (vans, bookings)
  const loadAllData = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      // Load data with consolidated error handling
      console.log("Loading all data for Fleet Management...");
      
      // Load vans
      const vansData = await fetchVans();
      setVans(vansData);
      
      // Load bookings
      const bookingsData = await fetchBookings();
      setBookings(bookingsData);
      
      // Clear assignment cache to force fresh fetches
      loadedAssignmentsRef.current = {};
      
      // Update each booking with its assignment info - fetch for all bookings to get current state
      const assignmentPromises = [];
      for (const booking of bookingsData) {
        assignmentPromises.push(
          fetchAssignmentsForBooking(booking.id)
            .then(bookingAssignments => {
              console.log(`Loaded ${bookingAssignments.length} assignments for booking ${booking.id}`);
              return { id: booking.id, assignments: bookingAssignments };
            })
            .catch(error => {
              console.error(`Error fetching assignments for booking ${booking.id}:`, error);
              return { id: booking.id, assignments: [] };
            })
        );
      }
      
      // Wait for all assignment fetches to complete
      const assignmentResults = await Promise.all(assignmentPromises);
      
      // Update assignments state once with all the new data
      const newAssignments = {};
      assignmentResults.forEach(result => {
        if (result) {
          newAssignments[result.id] = result.assignments;
          // Mark as loaded
          loadedAssignmentsRef.current[result.id] = true;
        }
      });
      
      setAssignments(newAssignments);
      console.log("Final assignments state:", newAssignments);
      
      // Fetch van blocks
      const vanBlocksData = await fetchVanBlocks();
      setVanBlocks(vanBlocksData);
      
      // Update last refresh time
      setLastRefreshTime(new Date());
      console.log("All data loaded successfully");
    } catch (error) {
      console.error("Error loading data:", error);
      setError(`Failed to load data: ${error.message}`);
      
      // Show a toast error
      toast.error(`Failed to load data: ${error.message}`);
    } finally {
      setIsLoading(false);
    }
  }, [fetchVans, fetchBookings, fetchAssignmentsForBooking, fetchVanBlocks]);

  // Unassign a booking
  const unassignBooking = async (bookingId) => {
    if (!(await setupAuth())) {
      toast.error("Authentication required");
      return;
    }
    
    setIsUnassigning(true);
    
    try {
      // Delete all assignments for this booking
      const response = await axios.delete(`${BASE_URL}/api/bookings/${bookingId}/assignments`);
      
      if (response.status === 200) {
        toast.success("Booking unassigned successfully");
        
        // Update assignments state immediately instead of re-fetching all data
        setAssignments(prev => {
          const newAssignments = { ...prev };
          delete newAssignments[bookingId];
          return newAssignments;
        });
        
        // Also update the loadedAssignmentsRef to allow re-fetching if needed
        loadedAssignmentsRef.current[bookingId] = false;
      } else {
        throw new Error("Failed to unassign booking");
      }
    } catch (error) {
      console.error("Error unassigning booking:", error);
      toast.error("Failed to unassign booking. Please try again.");
    } finally {
      setIsUnassigning(false);
    }
  };

  // Open assignment modal
  const openAssignModal = (booking) => {
    setSelectedBooking(booking);
    setSelectedVan(null);
    setIsAssignModalOpen(true);
  };

  // Toggle expanded state for van
  const toggleVanExpand = (vanId) => {
    setExpandedVans(prev => ({
      ...prev,
      [vanId]: !prev[vanId]
    }));
  };

  // Delete a van block
  const deleteVanBlock = async (blockId) => {
    if (!(await setupAuth())) {
      toast.error("Authentication required");
      return;
    }

    try {
      console.log(`Deleting van block ${blockId}...`);
      const response = await axios.delete(`${BASE_URL}/api/fleet/vans/blocks/${blockId}`);
      
      if (response.status === 200) {
        toast.success("Van block deleted successfully");
        // Refresh data to show updated blocks
        loadAllData();
      } else {
        throw new Error("Failed to delete van block");
      }
    } catch (error) {
      console.error("Error deleting van block:", error);
      toast.error("Failed to delete van block. Please try again.");
    }
  };

  // Delete a booking (with confirmation)
  const deleteBooking = useCallback(async (bookingId, bookingName) => {
    console.log(`🗑️ DELETE BOOKING FUNCTION CALLED`);
    console.log(`🗑️ Delete booking called: ID=${bookingId}, Name=${bookingName}`);
    console.log(`🗑️ Function parameters:`, { bookingId, bookingName });
    console.log(`🗑️ Current bookings count:`, bookings.length);
    
    // Show confirmation dialog
    const confirmDelete = window.confirm(
      `Are you sure you want to delete booking #${bookingId} for ${bookingName}?\n\nThis action cannot be undone and will remove all associated assignments.`
    );
    
    if (!confirmDelete) {
      console.log("❌ Delete cancelled by user");
      return;
    }

    console.log(`🗑️ Delete confirmed by user, proceeding...`);

    if (!(await setupAuth())) {
      console.log("❌ Authentication failed");
      toast.error("Authentication required");
      return;
    }
    
    console.log(`🗑️ Authentication successful, setting loading state...`);
    setIsLoading(true);
    
    try {
      console.log(`🗑️ Starting deletion process for booking ${bookingId}...`);
      
      // First, delete any assignments for this booking
      console.log(`🗑️ Step 1: Deleting assignments for booking ${bookingId}...`);
      const deleteAssignmentsResponse = await axios.delete(
        `${BASE_URL}/api/bookings/${bookingId}/assignments`,
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem("admin_token") || "admin_token"}`
          }
        }
      );
      console.log(`✅ Assignments deleted successfully:`, deleteAssignmentsResponse.status);
      
      // Then delete the booking itself
      console.log(`🗑️ Step 2: Deleting booking ${bookingId}...`);
      const response = await axios.delete(
        `${BASE_URL}/api/bookings/${bookingId}`,
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem("admin_token") || "admin_token"}`
          }
        }
      );
      
      console.log(`✅ Booking deletion response:`, response.status, response.data);
      
      if (response.status === 200) {
        console.log(`🎉 Booking ${bookingId} deleted successfully!`);
        toast.success(`Booking #${bookingId} deleted successfully`);
        
        // Update local state immediately
        setBookings(prev => {
          const updated = prev.filter(booking => booking.id !== bookingId);
          console.log(`📊 Updated bookings: ${prev.length} -> ${updated.length}`);
          return updated;
        });
        
        // Remove assignments from state
        setAssignments(prev => {
          const newAssignments = { ...prev };
          delete newAssignments[bookingId];
          return newAssignments;
        });
        
        // Also update the loadedAssignmentsRef
        if (loadedAssignmentsRef.current) {
          delete loadedAssignmentsRef.current[bookingId];
        }
        
        console.log(`✅ Successfully deleted booking ${bookingId}`);
      } else {
        throw new Error(`Unexpected response status: ${response.status}`);
      }
    } catch (error) {
      console.error("❌ Error deleting booking:", error);
      console.error("❌ Error response:", error.response);
      
      let errorMessage = "Failed to delete booking. Please try again.";
      
      if (error.response?.data?.error) {
        errorMessage = error.response.data.error;
      } else if (error.response?.status === 404) {
        errorMessage = "Booking not found. It may have already been deleted.";
        // Still remove from local state if it was already deleted
        setBookings(prev => {
          const updated = prev.filter(booking => booking.id !== bookingId);
          console.log(`📊 Removed missing booking ${bookingId} from state: ${prev.length} -> ${updated.length}`);
          return updated;
        });
        setAssignments(prev => {
          const newAssignments = { ...prev };
          delete newAssignments[bookingId];
          return newAssignments;
        });
      } else if (error.response?.status === 429) {
        errorMessage = "Too many requests. Please wait a moment and try again.";
      } else if (error.code === 'NETWORK_ERROR' || !error.response) {
        errorMessage = "Network error. Please check your connection and try again.";
      }
      
      toast.error(errorMessage);
    } finally {
      console.log("🏁 Delete operation completed, clearing loading state");
      setIsLoading(false);
    }
  }, [bookings.length]); // Dependencies: only recreate if bookings count changes

  // Debug deleteBooking function creation
  console.log('🔄 deleteBooking function created:', {
    timestamp: new Date().toISOString(),
    functionId: deleteBooking.toString().slice(0, 100) + '...'
  });

  // Assign vans to a booking
  const assignVanToBooking = async (bookingId, vanAssignments) => {
    console.log("🚀 Starting assignment process (simplified)...");
    console.log("Booking ID:", bookingId, "Van assignments:", vanAssignments);
    
    if (!(await setupAuth())) {
      toast.error("Authentication required");
      return;
    }

    setIsLoading(true);
    
    try {
      // Use the same simple approach as the test assignment
      const assignments = vanAssignments.map(vanId => ({
        booking_id: bookingId,
        van_id: vanId
      }));
      
      console.log("📝 Creating assignments:", assignments);
      
      const response = await axios.post(
        `${BASE_URL}/api/bookings/${bookingId}/assignments`, 
        { assignments },
        { 
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem("admin_token") || "admin_token"}`
          }
        }
      );
      
      console.log("✅ Assignment response:", response.data);
      toast.success(`Vans assigned successfully to booking ${bookingId}`);
      
      // Close the modal
      setIsAssignModalOpen(false);
      
      // Refresh all data (same as test assignment)
      loadAllData();
      
    } catch (error) {
      console.error("💥 Assignment error:", error);
      console.error("📄 Error response:", error.response?.data);
      
      let errorMessage = "Failed to assign vans. Please try again.";
      
      if (error.response?.data?.errors) {
        const errors = error.response.data.errors;
        if (errors.includes("is already booked during this time period")) {
          errorMessage = "The selected van(s) are already booked during this time. Please choose different vans.";
        } else if (errors.some(err => err.includes("is blocked during this time period"))) {
          errorMessage = "The selected van(s) are blocked for maintenance during this time. Please choose different vans.";
        } else {
          errorMessage = `Assignment failed: ${errors.join(', ')}`;
        }
      } else if (error.response?.data?.error) {
        errorMessage = error.response.data.error;
      }
      
      toast.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  // Handle bulk van blocking
  const handleBulkVanBlocking = async (blockPromises) => {
    if (!(await setupAuth())) {
      toast.error("Authentication required");
      return;
    }

    try {
      console.log("🔄 Creating bulk van blocks...", blockPromises);
      
      const response = await axios.post(
        `${BASE_URL}/api/fleet/vans/blocks/bulk`, 
        { blocks: blockPromises },
        { 
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem("admin_token") || "admin_token"}`
          }
        }
      );
      
      console.log("✅ Bulk blocking response:", response.data);
      
      // Handle different response scenarios
      if (response.status === 200) {
        // All blocks created successfully
        toast.success(`Successfully blocked ${response.data.successful} van(s)`);
      } else if (response.status === 207) {
        // Partial success
        const { successful, failed, errors } = response.data;
        toast.success(`${successful} van(s) blocked successfully`);
        
        if (failed > 0) {
          // Show details of failures
          const failureMessages = errors.map(err => 
            `${err.vanName || `Van ${err.vanId}`}: ${err.error}`
          ).join('\n');
          console.warn("Some blocks failed:", failureMessages);
          toast.error(`${failed} van(s) could not be blocked. Check console for details.`);
        }
      }
      
      // Refresh data to show updated blocks
      loadAllData();
      
    } catch (error) {
      console.error("💥 Bulk blocking error:", error);
      console.error("📄 Error response:", error.response?.data);
      
      let errorMessage = "Failed to create van blocks. Please try again.";
      
      if (error.response?.data?.errors) {
        const errors = error.response.data.errors;
        const conflictVans = errors
          .filter(err => err.conflicts)
          .map(err => err.vanName || `Van ${err.vanId}`)
          .join(', ');
        
        if (conflictVans) {
          errorMessage = `Cannot block ${conflictVans}: Already booked or blocked during this time period.`;
        } else {
          errorMessage = `Blocking failed: ${errors.map(e => e.error).join(', ')}`;
        }
      } else if (error.response?.data?.error) {
        errorMessage = error.response.data.error;
      }
      
      toast.error(errorMessage);
      throw error; // Re-throw so the modal can handle it
    }
  };

  // Initial data load
  useEffect(() => {
    console.log("FleetManagement component mounted");
    
    // Load data immediately
    loadAllData();
    
    // Set up polling interval (every 30 seconds) - but only once
    pollingIntervalRef.current = setInterval(() => {
      // Call loadAllData directly to avoid dependency issues
      console.log("Polling interval triggered - refreshing data...");
      loadAllData();
    }, 30000);
    
    // Cleanup on unmount
    return () => {
      console.log("FleetManagement component unmounting - clearing interval");
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
    };
  }, []); // Empty dependency array to run only once on mount

  return (
    <div className="container mx-auto p-4">
      <div className="mb-8 bg-gray-900 rounded-lg p-4 shadow-lg">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-4">
          <h1 className="text-2xl font-bold text-white mb-4 md:mb-0">Fleet Management</h1>
          <div className="flex flex-col sm:flex-row gap-2">
            <button
              onClick={loadAllData}
              className="px-4 py-2 bg-black border border-white/30 hover:border-white/50 text-white rounded-md flex items-center justify-center transition-colors"
              disabled={isLoading}
            >
              {isLoading ? (
                <span className="flex items-center">
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Loading...
                </span>
              ) : (
                <span className="flex items-center">
                  <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"></path>
                  </svg>
                  Refresh Data
                </span>
              )}
            </button>
          </div>
        </div>
        
        {error && (
          <div className="bg-red-900/30 border border-red-900 text-red-200 p-4 rounded-lg mb-6">
            <h3 className="font-bold mb-2">Error</h3>
            <p>{error}</p>
          </div>
        )}
        
        <div className="text-gray-400 text-sm">
          Last updated: {new Date(lastRefreshTime).toLocaleString('en-GB', { timeZone: 'Europe/Athens' })}
        </div>
      </div>
      
      {/* Fleet Overview */}
      <div className="mb-8">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-bold text-white">Fleet Overview</h2>
          <button
            onClick={() => setIsBulkBlockModalOpen(true)}
            className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors flex items-center gap-2"
            disabled={isLoading || vans.length === 0}
          >
            🚫 Bulk Block Vans
          </button>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {vans.map(van => (
            <div key={van.id} className="bg-gray-900 border border-gray-800 rounded-lg overflow-hidden shadow-lg">
              <div 
                className="flex justify-between items-center p-4 cursor-pointer" 
                onClick={() => toggleVanExpand(van.id)}
              >
                <div className="flex items-center space-x-2">
                  <span className="text-2xl">🚐</span>
                  <div>
                    <h3 className="font-bold text-white">{van.name}</h3>
                    <p className="text-gray-400 text-sm">{van.license_plate}</p>
                  </div>
                </div>
                <div className="flex items-center">
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-900/50 text-blue-300 mr-2">
                    {van.capacity} seats
                  </span>
                  <button className="text-gray-400 hover:text-white">
                    {expandedVans[van.id] ? (
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 15l7-7 7 7"></path>
                      </svg>
                    ) : (
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path>
                      </svg>
                    )}
                  </button>
                </div>
              </div>
              
              {expandedVans[van.id] && (
                <div className="p-4 border-t border-gray-800 bg-black/20">
                  <div className="text-sm">
                    <h4 className="text-gray-400 font-medium mb-2">Van Status</h4>
                    <p className="text-white">
                      Status: <span className="text-green-400">{van.status}</span>
                    </p>
                    <p className="text-gray-400 mt-1">
                      Capacity: {van.capacity} passengers
                    </p>
                    
                    {/* Van blocks display */}
                    <div className="mt-4">
                      <h4 className="text-gray-400 font-medium mb-2">Availability Blocks</h4>
                      {vanBlocks.filter(block => block.van_id === van.id).length > 0 ? (
                        <div className="space-y-2">
                          {vanBlocks
                            .filter(block => block.van_id === van.id)
                            .map(block => (
                              <div key={block.id} className="bg-red-900/20 border border-red-800 rounded p-2">
                                <div className="flex justify-between items-start">
                                  <div className="flex-1">
                                    <p className="text-red-300 text-xs font-medium">{block.reason}</p>
                                    <p className="text-gray-400 text-xs mt-1">
                                      {new Date(block.start_time).toLocaleString('en-GB', {
                                        day: '2-digit',
                                        month: '2-digit',
                                        year: '2-digit',
                                        hour: '2-digit',
                                        minute: '2-digit',
                                        timeZone: 'Europe/Athens'
                                      })} - {new Date(block.end_time).toLocaleString('en-GB', {
                                        day: '2-digit',
                                        month: '2-digit',
                                        year: '2-digit',
                                        hour: '2-digit',
                                        minute: '2-digit',
                                        timeZone: 'Europe/Athens'
                                      })}
                                    </p>
                                  </div>
                                  <button
                                    onClick={() => deleteVanBlock(block.id)}
                                    className="text-red-400 hover:text-red-300 ml-2"
                                    title="Delete block"
                                  >
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"></path>
                                    </svg>
                                  </button>
                                </div>
                              </div>
                            ))}
                        </div>
                      ) : (
                        <p className="text-gray-500 text-xs">No availability blocks</p>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
      
      {/* Bookings Management */}
      <div className="mb-8">
        <div className="flex justify-between items-center mb-6">
          <div>
            <h2 className="text-2xl font-bold text-white mb-1">Bookings Management</h2>
            <p className="text-gray-400">Assign vans and manage active bookings</p>
          </div>
          <button
            onClick={loadAllData}
            disabled={isLoading}
            className="px-4 py-2 bg-black border border-white/30 hover:border-white/50 disabled:bg-gray-600 disabled:cursor-not-allowed text-white rounded-lg transition-colors flex items-center gap-2"
          >
            {isLoading ? (
              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
            ) : (
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
            )}
            Refresh
          </button>
        </div>
        
        {error && (
          <div className="bg-red-900/20 border border-red-700 rounded-lg p-4 mb-4">
            <div className="text-red-400 font-medium">Error loading data</div>
            <div className="text-red-300 text-sm mt-1">{error}</div>
            <button 
              onClick={loadAllData}
              className="mt-2 px-3 py-1 bg-red-700 hover:bg-red-600 text-white rounded text-sm"
            >
              Try Again
            </button>
          </div>
        )}
        
        {isLoadingBookings && (
          <div className="bg-gray-900/50 border border-gray-700 rounded-lg p-8 text-center mb-4">
            <div className="w-8 h-8 border-4 border-white border-t-transparent rounded-full animate-spin mx-auto mb-3"></div>
            <div className="text-gray-400">Loading bookings...</div>
          </div>
        )}
        
        {/* Render bookings using the FleetBookingsOrganized component */}
        <FleetBookingsOrganized
          bookings={bookings}
          assignments={assignments}
          vans={vans}
          openAssignModal={openAssignModal}
          unassignBooking={unassignBooking}
          deleteBooking={deleteBooking}
          isLoading={isLoading}
          isUnassigning={isUnassigning}
        />
      </div>
      
      {/* Van Assignment Modal */}
      {isAssignModalOpen && selectedBooking && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50">
          <div className="bg-gray-900 w-full max-w-3xl rounded-lg shadow-lg overflow-hidden">
            <div className="flex justify-between items-center p-4 border-b border-gray-800">
              <h3 className="text-lg font-medium text-white">
                Assign Vans to Booking
              </h3>
              <button 
                onClick={() => setIsAssignModalOpen(false)}
                className="text-gray-400 hover:text-white"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
                </svg>
              </button>
            </div>
            
            <div className="p-6">
              <div className="mb-6 bg-gray-800/50 p-4 rounded-lg">
                <h4 className="text-white font-medium mb-2">Booking Details</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div>
                    <p className="text-gray-400 text-sm">Booking ID:</p>
                    <p className="text-white">{selectedBooking.id}</p>
                  </div>
                  <div>
                    <p className="text-gray-400 text-sm">Customer:</p>
                    <p className="text-white">{selectedBooking.full_name}</p>
                  </div>
                  <div>
                    <p className="text-gray-400 text-sm">Datetime:</p>
                    <p className="text-white">{formatDateShort(selectedBooking.datetime)}</p>
                  </div>
                  <div>
                    <p className="text-gray-400 text-sm">Passengers:</p>
                    <p className="text-white">{selectedBooking.passengers} passengers</p>
                  </div>
                  <div>
                    <p className="text-gray-400 text-sm">Route:</p>
                    <p className="text-white">{selectedBooking.pickup_name} → {selectedBooking.dropoff_name}</p>
                  </div>
                </div>
              </div>
              
              <VanAssignmentForm 
                booking={selectedBooking}
                vans={vans}
                onSubmit={(vanIds) => assignVanToBooking(selectedBooking.id, vanIds)}
                isLoading={isLoading}
                currentAssignments={assignments[selectedBooking.id] || []}
              />
            </div>
          </div>
        </div>
      )}
      
      {/* Bulk Van Block Modal */}
      <BulkVanBlockModal
        isOpen={isBulkBlockModalOpen}
        onClose={() => setIsBulkBlockModalOpen(false)}
        vans={vans}
        onCreateBlocks={handleBulkVanBlocking}
      />
    </div>
  );
}

export default FleetManagement;